import { buyAgainCartMutation } from '@/api/carts/cartMutations';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/common/Accordion';
import { Button } from '@/components/common/Button';
import { Heading } from '@/components/common/Heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common/Table';
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/dashboard/DashboardCard';
import type { Order } from '@/domain/Order';
import { Role } from '@/domain/Role';
import { useHasRole } from '@/hooks/useHasRole';
import { cn } from '@/lib/cn';
import { routes } from '@/routes/routes';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

interface OrderCardProps {
  isDoctor?: boolean;
  order: Order;
}

const getDeliveryDate = (createdDate: string, daysToAdd: number) => {
  const date = new Date(createdDate);
  date.setDate(date.getDate() + daysToAdd);
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
};

export const OrderCard = ({ order, isDoctor = false }: OrderCardProps) => {
  const navigate = useNavigate();
  const placedDate = order.created_at?.split('T')[0] ?? 'N/A';

  const { hasRole } = useHasRole();

  const buyAgainMutation = useMutation({
    ...buyAgainCartMutation(order.cart_id),
    onSuccess: (newCart) => {
      navigate({
        to: routes.dashboard.myCarts.cart.items,
        params: { cartId: newCart.id },
      });
    },
  });

  const handleBuyAgain = () => {
    buyAgainMutation.mutate();
  };

  return (
    <Card className="lg:py-7.5">
      <CardHeader className={cn('lg:px-7.5', isDoctor && 'lg:px-0')}>
        <div className="flex w-full flex-wrap items-start justify-between gap-4">
          <p className="font-medium">Order: #{order.id}</p>
          <p>Placed on: {placedDate}</p>
          <p>Total: {(order.price / 100).toFixed(2)} €</p>
          <p>Subscription period: {order.period} month(s)</p>
        </div>
      </CardHeader>

      <CardContent className={cn('lg:px-7.5', isDoctor && 'lg:px-0')}>
        <Accordion type="single" collapsible>
          <AccordionItem value={`order-${order.id}`} className="border-0">
            <div className="mb-4 flex items-center justify-between">
              <Heading
                asChild
                size="h5"
                className={cn(
                  'text-left',
                  order.state === 'Paid' && 'text-green-600',
                  order.state === 'Approved' && 'text-accent',
                  order.state === 'Failed' && 'text-destructive',
                )}
              >
                <p>{order.state}</p>
              </Heading>

              <AccordionTrigger className="py-0 text-base hover:no-underline [&>svg]:hidden [&[data-state=open]_.expand-text]:hidden [&[data-state=open]_.hide-text]:inline">
                <span className="text-primary hover:text-secondary expand-text font-medium transition-colors">
                  Expand
                </span>
                <span className="text-primary hover:text-secondary hide-text hidden font-medium transition-colors">
                  Hide
                </span>
              </AccordionTrigger>
            </div>

            <p className="font-medium">
              Expected delivery date:{' '}
              {order.created_at
                ? `${getDeliveryDate(order.created_at, 3)} - ${getDeliveryDate(order.created_at, 7)}`
                : 'N/A'}
            </p>

            <AccordionContent>
              <div className="mt-4 flex flex-col gap-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplement</TableHead>
                      <TableHead>Dose</TableHead>
                      <TableHead className="text-center">30 pills</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {order.items.map((item, index) => (
                      <TableRow key={item.id || index}>
                        <TableCell>{item.name || 'N/A'}</TableCell>
                        <TableCell>{item.strength || 'N/A'}</TableCell>
                        <TableCell className="text-center">
                          {item.quantity || 0}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="flex flex-wrap gap-3">
                  {order.parcel_track && (
                    <Button asChild variant="secondary">
                      <a
                        href={`https://www.post.lt/siuntu-sekimas?parcels=${order.parcel_track}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Track shipment
                      </a>
                    </Button>
                  )}

                  {hasRole(Role.CUSTOMER) && (
                    <Button
                      onClick={handleBuyAgain}
                      disabled={buyAgainMutation.isPending}
                    >
                      {buyAgainMutation.isPending
                        ? 'Duplicating...'
                        : 'Buy again'}
                    </Button>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
};
